/**
 * Visual themes by level band (the CLAUDE.md table).
 *
 * Pure data: no Phaser imports. This is the single source of truth - the level
 * generator reads it, and the renderer reads the palettes to build its parallax
 * layers. Prompt 10 swaps in Blender SVGs by filling in the manifest under the
 * same `backgrounds/{env}/{layer}` keys.
 */
import type { BackgroundTheme, PlatformTheme } from '../types';

/** Weather and light effects that sit in front of the parallax. */
export type AmbientEffect = 'none' | 'snow' | 'blizzard' | 'aurora' | 'bubbles' | 'dust';

export interface ThemeBand {
  /** Inclusive level range. */
  readonly fromLevel: number;
  readonly toLevel: number;
  readonly background: BackgroundTheme;
  readonly platform: PlatformTheme;
  /** Levels 31-40 are glowing floes rather than a separate asset set. */
  readonly glow: boolean;
  readonly ambient: AmbientEffect;
}

export const THEME_BANDS: readonly ThemeBand[] = [
  {
    fromLevel: 1,
    toLevel: 10,
    background: 'sunset',
    platform: 'ice-floe',
    glow: false,
    ambient: 'snow',
  },
  {
    fromLevel: 11,
    toLevel: 20,
    background: 'midday-glacier',
    platform: 'cloud',
    glow: false,
    ambient: 'none',
  },
  {
    fromLevel: 21,
    toLevel: 30,
    background: 'snowstorm-dusk',
    platform: 'marshmallow',
    glow: false,
    ambient: 'snow',
  },
  {
    fromLevel: 31,
    toLevel: 40,
    background: 'aurora-night',
    platform: 'ice-floe',
    glow: true,
    ambient: 'aurora',
  },
  {
    fromLevel: 41,
    toLevel: 50,
    background: 'iceberg-sea',
    platform: 'soccer-field',
    glow: false,
    ambient: 'bubbles',
  },
  {
    fromLevel: 51,
    toLevel: 60,
    background: 'research-station',
    platform: 'game-day',
    glow: false,
    ambient: 'snow',
  },
  {
    fromLevel: 61,
    toLevel: 70,
    background: 'ice-cave',
    platform: 'cloud',
    glow: false,
    ambient: 'dust',
  },
  {
    fromLevel: 71,
    toLevel: 80,
    background: 'blizzard',
    platform: 'marshmallow',
    glow: false,
    ambient: 'blizzard',
  },
  {
    fromLevel: 81,
    toLevel: 90,
    background: 'midnight-sun',
    platform: 'soccer-field',
    glow: false,
    ambient: 'none',
  },
  // Mixed uses all five platform looks, so no blanket glow (only 31-40 glow).
  {
    fromLevel: 91,
    toLevel: 100,
    background: 'grand-aurora',
    platform: 'mixed',
    glow: false,
    ambient: 'aurora',
  },
];

export function themeBandFor(level: number): ThemeBand {
  const safe = Number.isFinite(level) ? Math.round(level) : 1;
  const clamped = Math.min(100, Math.max(1, safe));
  const band = THEME_BANDS.find((row) => clamped >= row.fromLevel && clamped <= row.toLevel);
  return band ?? (THEME_BANDS[0] as ThemeBand);
}

/** Concrete platform looks drawn from when a band is 'mixed'. */
export const MIXED_PLATFORM_POOL: readonly PlatformTheme[] = [
  'ice-floe',
  'cloud',
  'marshmallow',
  'soccer-field',
  'game-day',
];

/* ------------------------------------------------------------- palettes */

export type GradientStops = ReadonlyArray<readonly [color: number, position: number]>;

export interface EnvironmentPalette {
  /** Back layer: a full-height vertical gradient. */
  readonly sky: GradientStops;
  /** Middle layer silhouettes (ice shelves, mountains, cave walls). */
  readonly far: number;
  readonly farAccent: number;
  /** Front layer: drifts and foreground shapes, tiled horizontally. */
  readonly near: number;
  readonly nearAccent: number;
  /** A sun or moon disc, or null where there is none. */
  readonly sun: { readonly color: number; readonly glow: number; readonly y: number } | null;
  /** Tint for ambient particles. */
  readonly ambientTint: number;
}

/**
 * One palette per environment. The sunset is the fully art-directed one
 * (CLAUDE.md calls it out: deep purple to magenta to orange to a gold horizon);
 * the rest are coherent stand-ins until the Blender backgrounds land.
 */
export const ENVIRONMENTS: Readonly<Record<BackgroundTheme, EnvironmentPalette>> = {
  sunset: {
    sky: [
      [0x2b1b45, 0],
      [0x6b2d5c, 0.38],
      [0xd1495b, 0.64],
      [0xff7b54, 0.84],
      [0xffc145, 1],
    ],
    far: 0x6a3d6e,
    farAccent: 0x8a5484,
    near: 0x3d2352,
    nearAccent: 0x7a4a86,
    // Low, and just above the ridge line so it reads as setting behind it.
    sun: { color: 0xfff3c4, glow: 0xffb703, y: 0.52 },
    ambientTint: 0xffe3b0,
  },
  'midday-glacier': {
    sky: [
      [0x4ea8de, 0],
      [0x90e0ef, 0.45],
      [0xcaf0f8, 0.8],
      [0xf7fbff, 1],
    ],
    far: 0x9ad1e8,
    farAccent: 0xd7f0fb,
    near: 0xe8f6ff,
    nearAccent: 0xffffff,
    sun: { color: 0xffffff, glow: 0xfff3c4, y: 0.16 },
    ambientTint: 0xffffff,
  },
  'snowstorm-dusk': {
    sky: [
      [0x232946, 0],
      [0x3c4a6b, 0.45],
      [0x6b7a99, 0.8],
      [0x9aa7bf, 1],
    ],
    far: 0x4a5a7a,
    farAccent: 0x66789b,
    near: 0x2b3650,
    nearAccent: 0x8fa0bf,
    sun: null,
    ambientTint: 0xe8eefc,
  },
  'aurora-night': {
    sky: [
      [0x05060f, 0],
      [0x0b1a35, 0.4],
      [0x11324f, 0.75],
      [0x1b4a63, 1],
    ],
    far: 0x14263f,
    farAccent: 0x2e5f6e,
    near: 0x0a1526,
    nearAccent: 0x6fe3c7,
    sun: { color: 0xf2f6ff, glow: 0x9fd8ff, y: 0.2 },
    ambientTint: 0x8ef5d8,
  },
  'iceberg-sea': {
    sky: [
      [0x0d3b66, 0],
      [0x1f6f9c, 0.4],
      [0x4fb3d9, 0.72],
      [0xa9dff0, 1],
    ],
    far: 0x1b5f88,
    farAccent: 0x8fd6ef,
    near: 0x0a2d49,
    nearAccent: 0x7fd4f0,
    sun: { color: 0xfff6d8, glow: 0xffd166, y: 0.22 },
    ambientTint: 0xbfeaff,
  },
  'research-station': {
    sky: [
      [0x1a1f2e, 0],
      [0x2d3a52, 0.42],
      [0x4a5e80, 0.75],
      [0x7d93b5, 1],
    ],
    far: 0x33425c,
    farAccent: 0xff8c42,
    near: 0x1f2a3d,
    nearAccent: 0xffc145,
    sun: null,
    ambientTint: 0xdfe8f7,
  },
  'ice-cave': {
    sky: [
      [0x04121c, 0],
      [0x0a2a3d, 0.4],
      [0x14506b, 0.74],
      [0x2b7f9e, 1],
    ],
    far: 0x0d3a4f,
    farAccent: 0x3f9fbf,
    near: 0x061a26,
    nearAccent: 0x7fdfff,
    sun: null,
    ambientTint: 0xa9e6ff,
  },
  blizzard: {
    sky: [
      [0x6b7a90, 0],
      [0x8c9bb0, 0.4],
      [0xb3c0d1, 0.75],
      [0xdce5ef, 1],
    ],
    far: 0x9aa8bc,
    farAccent: 0xc8d4e3,
    near: 0xe4ecf5,
    nearAccent: 0xffffff,
    sun: null,
    ambientTint: 0xffffff,
  },
  'midnight-sun': {
    sky: [
      [0x3b2a5a, 0],
      [0x8a4b73, 0.36],
      [0xe0796a, 0.62],
      [0xffb26b, 0.84],
      [0xffe29a, 1],
    ],
    far: 0x7a4a72,
    farAccent: 0xffb26b,
    near: 0x452c55,
    nearAccent: 0xffd9a0,
    sun: { color: 0xfff1c9, glow: 0xff9e4f, y: 0.56 },
    ambientTint: 0xffe6c0,
  },
  'grand-aurora': {
    sky: [
      [0x04040d, 0],
      [0x101b3a, 0.36],
      [0x1b3b5e, 0.68],
      [0x2f6e7a, 1],
    ],
    far: 0x14284a,
    farAccent: 0x7af5d0,
    near: 0x080f1f,
    nearAccent: 0xc08bff,
    sun: { color: 0xffffff, glow: 0xc08bff, y: 0.18 },
    ambientTint: 0xb6ffe8,
  },
};

/** What each world is called on the level map's signposts. */
export const WORLD_NAMES: Readonly<Record<BackgroundTheme, string>> = {
  sunset: 'Sunset Shore',
  'midday-glacier': 'Glacier Valley',
  'snowstorm-dusk': 'Snowstorm Ridge',
  'aurora-night': 'Aurora Plains',
  'iceberg-sea': 'Iceberg Sea',
  'research-station': 'Research Station',
  'ice-cave': 'Crystal Cave',
  blizzard: 'Blizzard Pass',
  'midnight-sun': 'Midnight Sun Peaks',
  'grand-aurora': 'The South Pole',
};

export function paletteFor(background: BackgroundTheme): EnvironmentPalette {
  return ENVIRONMENTS[background];
}

/** Asset keys the renderer asks for, so Prompt 10 can fill them in the manifest. */
export function backgroundLayerKey(
  background: BackgroundTheme,
  layer: 'sky' | 'far' | 'near',
): string {
  return `backgrounds/${background}/${layer}`;
}

/** Parallax scroll factors, back to front. */
export const LAYER_SCROLL = { sky: 0.05, far: 0.12, near: 0.26 } as const;
